import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';
import { Util } from '@grandvisionhq/forms';
import '../models/cart/cart-state.js';
import '../models/checkout/checkout-step-error-type.js';
import '../models/checkout-settings.js';
import { ShippingMethod } from '../models/step-shipping/shipping-methods.js';
import '../models/shared.js';
import { getShipping, isConsentGiven, getConsents } from '../selectors.js';

const { getValuesFromState  } = Util;
/**
 * Get user selections
 */ const getSelectedShippingMethod = createSelector(getShipping, (shipping)=>shipping && shipping.selectedShippingMethod
);
const getSelectedStore = createSelector(getShipping, (shipping)=>shipping && shipping.selectedStore ? shipping.selectedStore : undefined
);
const getSelectedPickupPoint = createSelector(getShipping, (shipping)=>shipping && shipping.selectedPickupPoint ? shipping.selectedPickupPoint : undefined
);
const shouldValidateMyAccount = createSelector(getShipping, (shipping)=>shipping && shipping.shouldValidateMyAccount ? shipping.shouldValidateMyAccount : false
);
const shouldValidateServiceStore = createSelector(getShipping, (shipping)=>shipping && shipping.shouldValidateServiceStore ? shipping.shouldValidateServiceStore : false
);
/**
 * Get user's shipping details state
 */ const getShippingDetails = createSelector(getShipping, (shipping)=>shipping && shipping.shippingDetails
);
/**
 * These selectors either return the raw form values of the form data or undefined
 * If they return undefined it could also mean the user didn't toggle this part of the step in the checkout
 */ const dataOrUndefined = (form)=>form && form.isEnabled ? getValuesFromState(form.formData) : undefined
;
createSelector(getShippingDetails, (details)=>{
    return Boolean(details === null || details === void 0 ? void 0 : details.contactDetails);
});
createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.contactDetails)
);
createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.expressCheckout)
);
const isBillingAndShippingAddressSame = createSelector(getShipping, (shipping)=>shipping && shipping.isBillingAndDeliveryAddressSame || false
);
const isDeliveryFormEnabled = createSelector(getShippingDetails, (details)=>details && details.deliveryAddress && details.deliveryAddress.isEnabled
);
const getBillingAddress = createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.billingAddress)
);
createSelector(getShippingDetails, (details)=>{
    return Boolean(details === null || details === void 0 ? void 0 : details.billingAddress);
});
createSelector(getShippingDetails, (details)=>details && details.deliveryAddress && !isEmpty(details.deliveryAddress.formData)
);
const getDeliveryAddress = createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.deliveryAddress)
);
const hasStoredDeliveryAddress = createSelector(getShippingDetails, (details)=>{
    var ref;
    const values = details && getValuesFromState(details === null || details === void 0 ? void 0 : (ref = details.deliveryAddress) === null || ref === void 0 ? void 0 : ref.formData);
    return !values ? false : Object.keys(values).some((key)=>values[key]
    );
});
const getCompany = createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.company)
);
createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.invoice)
);
/**
 * Get only the values from all currently enabled shipping forms
 */ const getShippingData = createSelector(getShippingDetails, (details)=>details && Object.entries(details).reduce((filtered, [name, form])=>{
        if (form.isEnabled) {
            filtered[name] = getValuesFromState(form.formData);
        }
        return filtered;
    }, {})
);
/**
 * Shipping step validity state
 */ const isShippingStepValid = createSelector(getSelectedShippingMethod, getSelectedStore, getSelectedPickupPoint, getShippingDetails, isConsentGiven, getConsents, (shippingMethod, store, pickupPoint, details, consentGiven, consents)=>{
    return !!details && Object.entries(details).filter(([key])=>key !== 'expressCheckout'
    ).filter(([, form])=>form.isEnabled
    ).every(([, form])=>form.isValid
    ) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.Pickup ? store !== undefined : true) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.PickupPoint ? pickupPoint !== undefined : true) && (consents && consentGiven ? consentGiven : false) && !!(shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.isValid);
});
const isExpressCheckoutValid = createSelector(getSelectedShippingMethod, getSelectedStore, getSelectedPickupPoint, isConsentGiven, getShippingDetails, getConsents, (shippingMethod, store, pickupPoint, consentGiven, details, consents)=>{
    var ref, ref1;
    return !!details && // some opcos don't have additional express checkout fields. In that case, it's valid by default.
    (isEmpty(details === null || details === void 0 ? void 0 : (ref = details.expressCheckout) === null || ref === void 0 ? void 0 : ref.formData) || ((ref1 = details.expressCheckout) === null || ref1 === void 0 ? void 0 : ref1.isValid)) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.Pickup ? store !== undefined : true) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.PickupPoint ? pickupPoint !== undefined : true) && (consents && consentGiven ? consentGiven : false) && !!(shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.isValid);
});
const isPrefillFinished = createSelector(getShipping, (shipping)=>{
    return Boolean(shipping === null || shipping === void 0 ? void 0 : shipping.isPrefillFinished);
});
const getSelectedPostalCode = createSelector(getSelectedShippingMethod, getSelectedStore, getSelectedPickupPoint, getShippingData, (shippingMethod, selectedStore, selectedPickupPoint, shippingData)=>{
    switch(shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method){
        case 'dpd-priority-delivery':
        case 'home-delivery-express':
        case 'home-delivery':
            var ref, ref2;
            var ref3;
            return (ref3 = shippingData === null || shippingData === void 0 ? void 0 : (ref = shippingData.deliveryAddress) === null || ref === void 0 ? void 0 : ref.postalCode) !== null && ref3 !== void 0 ? ref3 : shippingData === null || shippingData === void 0 ? void 0 : (ref2 = shippingData.billingAddress) === null || ref2 === void 0 ? void 0 : ref2.postalCode;
        case 'pick-up':
            return selectedStore === null || selectedStore === void 0 ? void 0 : selectedStore.postalCode;
        case 'pick-up-point':
            return selectedPickupPoint === null || selectedPickupPoint === void 0 ? void 0 : selectedPickupPoint.postalCode;
        default:
            var ref4, ref5;
            var ref6;
            return (ref6 = shippingData === null || shippingData === void 0 ? void 0 : (ref4 = shippingData.deliveryAddress) === null || ref4 === void 0 ? void 0 : ref4.postalCode) !== null && ref6 !== void 0 ? ref6 : shippingData === null || shippingData === void 0 ? void 0 : (ref5 = shippingData.billingAddress) === null || ref5 === void 0 ? void 0 : ref5.postalCode;
    }
});

export { getBillingAddress, getCompany, getDeliveryAddress, getSelectedPickupPoint, getSelectedPostalCode, getSelectedShippingMethod, getSelectedStore, getShippingData, getShippingDetails, hasStoredDeliveryAddress, isBillingAndShippingAddressSame, isDeliveryFormEnabled, isExpressCheckoutValid, isPrefillFinished, isShippingStepValid, shouldValidateMyAccount, shouldValidateServiceStore };
//# sourceMappingURL=selectors.js.map
